import React, { FC } from 'react'
import { Button, TButtonProps } from 'transfix-ui/components/Button'
import { Fab } from 'transfix-ui/components/Fab'
import { IFabProps } from 'transfix-ui/components/Fab/Fab'
import { SvgIcon } from 'transfix-ui/components/SvgIcon'
import { PropTypes } from 'transfix-ui/core'
import { useBreakpointContext } from 'utils/context/BreakpointContext'

export interface IPrimaryActionProps {
  /**
   * The label of the Primary Action Button
   */
  label: string
  /**
   * The icon of the Float Action Button (only shown on mobile)
   * @example
   * ```
   *  import { Add as AddIcon } from 'components/atoms'
   *  { fabIcon: AddIcon }
   * ```
   */
  fabIcon?: typeof SvgIcon
  /**
   * The color of the Floar Action Button (only shown on mobile)
   * @default 'primary'
   */
  fabColor?: PropTypes.Color
  /**
   * The icon of the Primary Action Button (only shown on desktop)
   */
  buttonIcon?: typeof SvgIcon
  /**
   * Custom props for the Desktop Button
   */
  desktopButtonProps?: Omit<TButtonProps, 'startIcon' | 'buttonStyle' | 'label' | 'onClick'>
  /**
   * Custom props for the Mobile Button
   */
  mobileButtonProps?: Omit<IFabProps, 'aria-label' | 'onClick' | 'color' | 'size'>
  /**
   * The function of the Primary Action Button when clicked
   */
  onClick?: () => void
}

export const ResponsivePrimaryAction: FC<IPrimaryActionProps> = ({
  label,
  fabIcon,
  fabColor = 'primary',
  buttonIcon: Icon,
  desktopButtonProps = {},
  mobileButtonProps = {},
  onClick: primaryClickHandler,
}) => {
  const { isMobile } = useBreakpointContext()
  const EmptyComponent = () => <></>
  const FabIconComponent = fabIcon || EmptyComponent

  // shoudl the main button for the desktop view be shown?
  const isPrimaryActionButtonVisible = !isMobile && label

  // shoudl the main button for the mobile view be shown?
  const isFloatActionButtonVisible = isMobile && fabIcon

  if (isPrimaryActionButtonVisible) {
    // when a icon is provided for use on Desktop
    if (Icon) {
      return (
        <Button
          startIcon={<Icon />}
          onClick={primaryClickHandler}
          size='large'
          {...desktopButtonProps}>
          {label}
        </Button>
      )
    }

    // when NO icon is provided
    return (
      <Button color='primary' onClick={primaryClickHandler} size='large' {...desktopButtonProps}>
        {label}
      </Button>
    )
  }

  // the Float Action Button (exclusive for mobile view)
  if (isFloatActionButtonVisible) {
    return (
      <Fab
        aria-label={label}
        onClick={primaryClickHandler}
        color={fabColor}
        size='large'
        {...mobileButtonProps}>
        <FabIconComponent />
      </Fab>
    )
  }

  return <></>
}
